import React, { useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Button, Typography, Grid, Box, IconButton, TextField, CircularProgress } from "@mui/material";
import { Cancel, CloudUpload } from "@mui/icons-material";
import GlobalDialogContent from "../GlobalDialogContent";
import { UplaodGallery } from "../../../utils/services/project/project";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../../redux/reducer/snackbarSlice";
import { closeDialog } from "../../../redux/reducer/dialogSlice";
import { setModuleRefresh } from "../../../redux/reducer/registration/RefreshSlice";

const UploadGallery: React.FC = () => {
    const dispatch = useDispatch();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [fileNames, setFileNames] = useState<{ name: string; id: string }[]>([]);
    const [isUploading, setIsUploading] = useState(false);

    const formik = useFormik<any>({
        initialValues: {
            documentTitle: "",
            documentDescription: "",
            attachments: [],
        },
        validationSchema: Yup.object({
            documentTitle: Yup.string().required("Title is required"),
            documentDescription: Yup.string().required("Description is required"),
            attachments: Yup.array().min(1, "Please upload at least one document"),
        }),
        onSubmit: async (values) => {
            setIsUploading(true);
            const reqPayload = new FormData();
            reqPayload.append('title', String(values?.documentTitle));
            reqPayload.append('description', String(values?.documentDescription));
            values.attachments.forEach((attachment: any) => {
                reqPayload.append('documents', attachment.file);
            });

            const { code, data, message } = await UplaodGallery(reqPayload);
            if (code === 200) {
                dispatch(showSnackbar({ type: "success", message: message || 'Documents uploaded Succesfully.' }));
                dispatch(setModuleRefresh({ moduleName: 'galleryUpload', refresh: true }));               
                
                dispatch(closeDialog());
            } else {
                dispatch(showSnackbar({ type: "error", message: message || 'Documents uploaded error.' }));

            }
            setIsUploading(false);
        },
    });

    const allowedFileTypes = [
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "image/jpeg",
        "image/png",
      ];
      

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files) return;
      
        const maxSize = 5 * 1024 * 1024; 
        const validFiles: { file: File }[] = [];
        let hasSizeError = false;
        let hasTypeError = false;
      
        Array.from(event.target.files).forEach((file) => {
          if (!allowedFileTypes.includes(file.type)) {
            hasTypeError = true;
            return;
          }
      
          if (file.size > maxSize) {
            hasSizeError = true;
            return;
          }
      
          validFiles.push({ file });
        });
      
        if (hasSizeError) {
          dispatch(
            showSnackbar({
              type: "error",
              message: "File size should not exceed 5MB",
            })
          );
          event.target.value = "";
          return;
        }
      
        if (hasTypeError) {
          dispatch(
            showSnackbar({
              type: "error",
              message: "Only PDF, DOCX, XLSX, JPEG, and PNG files are allowed",
            })
          );
          event.target.value = "";
          return;
        }
      
        if (validFiles.length > 0) {
          const existingFiles = formik.values.attachments;
          const uniqueFiles = [...existingFiles, ...validFiles].filter(
            (fileWrapper, index, self) =>
              index === self.findIndex((f) => f.file.name === fileWrapper.file.name)
          );
      
          formik.setFieldValue("attachments", uniqueFiles);
          setFileNames(uniqueFiles.map((f) => ({ name: f.file.name, id: f.file.name })));
        }
      
        event.target.value = "";
      };   

    const handleRemoveFile = (fileName: string) => {
        const updatedFiles = formik.values.attachments.filter(
            (fileWrapper: any) => fileWrapper.file.name !== fileName
        );
        formik.setFieldValue("attachments", updatedFiles);
        setFileNames(updatedFiles.map((f: any) => ({ name: f.file.name, id: f.file.name })));
    };

    return (
        <form onSubmit={formik.handleSubmit}>
            <GlobalDialogContent
                dialogBody={
                    <>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={12}>
                                    <TextField
                                        fullWidth
                                        label="Document Title"
                                        variant="outlined"
                                        name="documentTitle"
                                        size="small"
                                        value={formik.values.documentTitle}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.documentTitle && Boolean(formik.errors.documentTitle)}
                                        helperText={
                                            formik.touched.documentTitle && 
                                            typeof formik.errors.documentTitle === 'string' ? 
                                            formik.errors.documentTitle : 
                                            ''
                                          }
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Document Description"
                                        variant="outlined"
                                        multiline
                                        rows={3}
                                        size="small"
                                        name="documentDescription"
                                        value={formik.values.documentDescription}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.documentDescription && Boolean(formik.errors.documentDescription)}
                                        helperText={
                                            formik.touched.documentDescription && 
                                            typeof formik.errors.documentDescription === 'string' ? 
                                            formik.errors.documentDescription : 
                                            ''
                                          }
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        sx={{ color: "success.secondary" }}
                                        id="policyDocumentUpload5"
                                    >
                                        Select Documents to Upload
                                        <input
                                            type="file"
                                            accept=".pdf,.jpeg,.png"
                                            multiple
                                            hidden
                                            ref={fileInputRef}
                                            onChange={handleFileChange}
                                        />
                                    </Button>
                                </Grid>
                            </Grid>

                        {fileNames.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                {fileNames.map((file) => (
                                    <Box key={file.id} sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                                        <Typography variant="body2">{file.name}</Typography>
                                        <IconButton onClick={() => handleRemoveFile(file.name)} size="small" color="error">
                                            <Cancel fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Box>
                        )}
                        {formik.errors.attachments && formik.touched.attachments && (
                            <Typography color="error"  variant="body2">
                                {formik.errors.attachments as string}
                            </Typography>
                        )}
                    </>
                }
                dialogFooter={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Button 
                        type="submit" 
                        variant="contained" 
                        color="primary" 
                        startIcon={!isUploading ? <CloudUpload /> : null}
                        disabled={isUploading}
                      >
                        {isUploading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : (
                          'Upload'
                        )}
                      </Button>
                    </Box>
                  }
                  
            />
        </form>
    );
};

export default UploadGallery;
